import { useMemo, useState, type KeyboardEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import { useT } from '@/i18n';
import { Empty, ErrorBox, Ltr, QueryView } from '@/components/ui';
import { api } from '@/lib/api/endpoints';
import { qk } from '@/lib/api/keys';
import { useTranscript, useWs } from '@/lib/api/hooks';
import { timecode } from '@/lib/format';
import { downloadAuthed, safeName } from '@/lib/download';
import { ApiError } from '@/lib/api/client';
import { logError, logInfo } from '@/lib/log';
import type { Transcript, TranscriptWord } from '@/lib/api/types';
import { EpisodeHeader, useEpisodeContext } from './context';

export interface Paragraph {
  speaker: TranscriptWord['speaker_id'];
  start_ms: number;
  words: TranscriptWord[];
}

/** Split words into paragraphs on a speaker change, a pause over 2 s, or every 80 words. */
export function paragraphs(words: TranscriptWord[]): Paragraph[] {
  const out: Paragraph[] = [];
  let cur: Paragraph | null = null;
  let prevEnd = 0;
  for (const w of words) {
    if (!cur || w.speaker_id !== cur.speaker || w.start_ms - prevEnd > 2000 || cur.words.length >= 80) {
      cur = { speaker: w.speaker_id, start_ms: w.start_ms, words: [] };
      out.push(cur);
    }
    cur.words.push(w);
    prevEnd = w.end_ms;
  }
  return out;
}

const LOW_CONFIDENCE = 0.6;

function WordEditor({ word, value, onDone }: { word: TranscriptWord; value: string; onDone: (text: string | null) => void }) {
  const { t } = useT();
  const [text, setText] = useState(value);
  const key = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onDone(text.trim() || null);
    }
    if (e.key === 'Escape') onDone(null);
  };
  return (
    <input
      autoFocus
      className="input"
      aria-label={`${t('wordFix')} · ${word.text}`}
      dir="auto"
      value={text}
      onChange={(e) => setText(e.target.value)}
      onKeyDown={key}
      onBlur={() => onDone(text.trim() || null)}
      style={{ height: 28, width: `${Math.max(4, text.length + 2)}ch`, display: 'inline-block', padding: '0 6px', fontSize: 15 }}
    />
  );
}

function TranscriptView({ transcript, sessionId, fileBase }: { transcript: Transcript; sessionId: string; fileBase: string }) {
  const { t } = useT();
  const qc = useQueryClient();
  const w = useWs();
  const [fixes, setFixes] = useState<Map<number, string>>(new Map());
  const [editing, setEditing] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const paras = useMemo(() => paragraphs(transcript.words), [transcript.words]);
  const textDir = transcript.language === 'he' || transcript.language === 'ar' ? 'rtl' : 'ltr';
  const speakers = useMemo(() => [...new Set(transcript.words.map((x) => String(x.speaker_id ?? '')))], [transcript.words]);

  const finish = (word: TranscriptWord, text: string | null) => {
    setEditing(null);
    setFixes((m) => {
      const next = new Map(m);
      if (text === null || text === word.text) next.delete(word.idx);
      else next.set(word.idx, text);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const body = [...fixes].map(([idx, text]) => ({ idx, text }));
      const res = await api.pipeline.patchWords(transcript.id, body);
      logInfo('transcript', `saved ${body.length} fix(es) transcript=${transcript.id} → v${res.version}`);
      qc.setQueryData(qk.transcript(w, sessionId), res);
      setFixes(new Map());
      void qc.invalidateQueries({ queryKey: ['ws', w, 'drilldown'] });
    } catch (e) {
      logError('transcript', `save failed transcript=${transcript.id}`, e);
      if (e instanceof ApiError && e.status === 409) {
        // Someone saved a newer version: load it and keep the user's fixes visible for re-applying.
        setError(new ApiError(409, t('transcriptStale')));
        void qc.invalidateQueries({ queryKey: qk.transcript(w, sessionId) });
      } else setError(e);
    } finally {
      setSaving(false);
    }
  };

  const srt = async () => {
    try {
      await downloadAuthed(api.pipeline.transcriptSrtUrl(sessionId), `${fileBase}.srt`);
    } catch (e) {
      setError(e);
    }
  };

  return (
    <section className="card" aria-labelledby="tr-h">
      <div className="card-head wrap" style={{ display: 'flex', flexWrap: 'wrap' }}>
        <div className="stack" style={{ gap: 2, flex: '1 1 320px' }}>
          <h2 id="tr-h" className="h2">{t('transcriptTitle')} · {t('transcriptVersion', { n: transcript.version })}</h2>
          <span className="muted small">{t('transcriptSub')}</span>
        </div>
        <button type="button" className="btn btn-sm" onClick={srt}><Download size={13} aria-hidden />{t('downloadTranscriptSrt')}</button>
      </div>
      {fixes.size ? (
        <div className="row wrap" role="status" style={{ padding: '10px 18px', background: 'var(--blue-soft)', position: 'sticky', top: 0, zIndex: 2 }}>
          <span style={{ color: 'var(--blue-text)', fontWeight: 500 }}>{t('pendingFixes', { n: fixes.size })}</span>
          <button type="button" className="btn btn-sm push" onClick={() => setFixes(new Map())} disabled={saving}>{t('discardFixes')}</button>
          <button type="button" className="btn btn-sm btn-primary" onClick={save} disabled={saving}>{saving ? t('saving') : t('saveFixes')}</button>
        </div>
      ) : null}
      {error ? <div style={{ padding: '10px 18px' }}><ErrorBox error={error} /></div> : null}
      <div style={{ padding: '8px 18px 18px', display: 'grid', gap: 14 }}>
        {paras.map((p) => (
          <div key={p.words[0].idx} style={{ display: 'grid', gridTemplateColumns: '84px minmax(0,1fr)', gap: 12 }}>
            <div className="stack" style={{ gap: 2, paddingTop: 4 }}>
              <Ltr className="mono muted"><span style={{ fontSize: 11 }}>{timecode(p.start_ms)}</span></Ltr>
              <span className="small" style={{ fontWeight: 600 }}>{t('speaker', { n: speakers.indexOf(String(p.speaker ?? '')) + 1 })}</span>
            </div>
            <p dir={textDir} lang={transcript.language} style={{ margin: 0 }}>
              {p.words.map((word) =>
                editing === word.idx ? (
                  <WordEditor key={word.idx} word={word} value={fixes.get(word.idx) ?? word.text} onDone={(txt) => finish(word, txt)} />
                ) : (
                  <span key={word.idx}>
                    <button
                      type="button"
                      className="word"
                      data-low={word.confidence < LOW_CONFIDENCE}
                      data-fixed={fixes.has(word.idx)}
                      data-deleted={!!word.deleted}
                      title={`${timecode(word.start_ms)}${word.deleted ? ` · ${t('wordDeleted')}` : word.confidence < LOW_CONFIDENCE ? ` · ${t('lowConfidence')}` : ''}`}
                      onClick={() => setEditing(word.idx)}
                    >
                      {fixes.get(word.idx) ?? word.text}
                    </button>{' '}
                  </span>
                ),
              )}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

export function TranscriptPage() {
  const { t } = useT();
  const ctx = useEpisodeContext();
  const session = ctx.session.data;
  const ready = !!session && (session.transcript_id !== null || session.stages.some((s) => s.stage === 'transcribe' && s.status === 'succeeded'));
  const transcript = useTranscript(ctx.sessionId, ready);
  return (
    <div className="page" style={{ maxWidth: 1100 }}>
      <QueryView query={ctx.project}>
        {(project) =>
          !ctx.episode ? (
            <div className="alert alert-error">{t('episodeNotFound')}</div>
          ) : (
            <>
              <EpisodeHeader project={project} episode={ctx.episode} />
              {!ready || !ctx.sessionId ? (
                <div className="card"><Empty>{t('noTranscript')}</Empty></div>
              ) : (
                <QueryView query={transcript}>{(tr) => <TranscriptView transcript={tr} sessionId={ctx.sessionId!} fileBase={safeName(`${project.title}-${ctx.episode!.number}`)} />}</QueryView>
              )}
            </>
          )
        }
      </QueryView>
    </div>
  );
}
